import type { ShortTermRateWorkerProps } from 'features/dataLayers/generation/shortTermCorrosionRate/calculateShortTermRate.worker'
import type {
  ShortTermGridValues,
  ShortTermRateFeatureGridMetadata,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import type {
  GenerateShortTermCorrosionRateCdfProps,
  ShortTermCorrosionRateGenerationParams,
} from 'features/dataLayers/generation/shortTermCorrosionRate/useGenerateShortTermCorrosionRateCDFs'
import { GeckoComponentDataFrame, GeckoMeasurementUnit } from 'types'

export function getFeatureGridMetadata(
  cdf: GeckoComponentDataFrame
): ShortTermRateFeatureGridMetadata {
  return {
    xStartFeet: cdf.data.geometry.start.x.convertTo(GeckoMeasurementUnit.Feet)
      .num,
    yStartFeet: cdf.data.geometry.start.y.convertTo(GeckoMeasurementUnit.Feet)
      .num,
    binWidthFeet: cdf.data.binning.bin_size.width.convertTo(
      GeckoMeasurementUnit.Feet
    ).num,
    binHeightFeet: cdf.data.binning.bin_size.height.convertTo(
      GeckoMeasurementUnit.Feet
    ).num,
    xBinCount: cdf.data.binning.x_bin_count,
    yBinCount: cdf.data.binning.y_bin_count,
  }
}

export function createWorkerProps({
  targetCdf,
  targetInspection,
  comparisonCdf,
  comparisonInspection,
  params,
  targetThicknessValues,
  comparisonThicknessValues,
  targetDifferenceFromNominalValues,
}: GenerateShortTermCorrosionRateCdfProps & {
  params: ShortTermCorrosionRateGenerationParams
  targetThicknessValues: ShortTermGridValues
  comparisonThicknessValues: ShortTermGridValues
  targetDifferenceFromNominalValues: ShortTermGridValues
}): ShortTermRateWorkerProps {
  return {
    targetThicknessValues,
    comparisonThicknessValues,
    targetDifferenceFromNominalValues,
    targetGrid: getFeatureGridMetadata(targetCdf),
    comparisonGrid: getFeatureGridMetadata(comparisonCdf),
    measurementUnitId: targetCdf.data.params.measurement_unit.id,
    targetInspectionDate: targetInspection.data.date.toISOString(),
    comparisonInspectionDate: comparisonInspection.data.date.toISOString(),
    targetFeatureCoreDfnPercent: params.target_feature_core_dfn_percent,
    targetFeatureGrowDfnPercent: params.target_feature_grow_dfn_percent,
    minFeatureAreaFeetSquared: params.min_feature_area_feet_squared,
    targetFeatureMergeDistanceFeet: params.target_feature_merge_distance_feet,
    targetFeatureMaxRectAreaFeetSquared:
      params.target_feature_max_rect_area_feet_squared,
    targetFeatureRectPaddingFeet: params.target_feature_rect_padding_feet,
    priorSupportSearchRadiusFeet: params.prior_support_search_radius_feet,
    priorSupportTranslationStepBins: params.prior_support_translation_step_bins,
    priorSupportDistanceWeight: params.prior_support_distance_weight,
    priorSupportMinCoverageFraction: params.prior_support_min_coverage_fraction,
    priorSupportMissingCoverageWeight:
      params.prior_support_missing_coverage_weight,
    priorSupportMinValueCount: params.prior_support_min_value_count,
    includeFeatureDetails: false,
  }
}

export function getTransferableGridValueBuffers(
  ...values: readonly ShortTermGridValues[]
): Transferable[] {
  const buffers = new Set<ArrayBuffer>()

  for (const value of values) {
    if (value instanceof Float32Array && value.buffer instanceof ArrayBuffer) {
      buffers.add(value.buffer)
    }
  }

  return [...buffers]
}
